// CelebrationOverlay.js — Classy celebration overlay
// Place in: src/components/CelebrationOverlay.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// ── Elegant particle system ───────────────────────────────
function ParticleCanvas({ type }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = window.innerWidth;
    const H = canvas.height = window.innerHeight;

    // Birthday: gold/white sparkling stars
    // Anniversary: rose gold hearts and petals
    const isBday = type === 'birthday';
    // Note: particles always use the same colors - they look great on both themes

    const particles = Array.from({ length: 60 }, (_, i) => ({
      x: Math.random() * W,
      y: -20 - Math.random() * H * 0.5,
      size: Math.random() * (isBday ? 4 : 6) + 2,
      speedY: Math.random() * 1.5 + 0.5,
      speedX: (Math.random() - 0.5) * 0.8,
      opacity: Math.random() * 0.7 + 0.3,
      rotation: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 2,
      color: isBday
        ? ['#F5C518','#fff','#FFD700','#FFF8DC','#E8C547'][Math.floor(Math.random() * 5)]
        : ['#E8A4A4','#C97B7B','#F5C518','#FFB6C1','#fff'][Math.floor(Math.random() * 5)],
      twinkle: Math.random() * Math.PI * 2,
      twinkleSpeed: Math.random() * 0.08 + 0.02,
    }));

    function drawStar(ctx, x, y, size, color, opacity) {
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.fillStyle = color;
      ctx.shadowColor = color;
      ctx.shadowBlur = size * 3;
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
        const r = i % 2 === 0 ? size : size * 0.4;
        ctx.lineTo(x + r * Math.cos(angle), y + r * Math.sin(angle));
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    function drawHeart(ctx, x, y, size, color, opacity) {
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.fillStyle = color;
      ctx.shadowColor = color;
      ctx.shadowBlur = size * 2;
      ctx.beginPath();
      ctx.moveTo(x, y + size * 0.3);
      ctx.bezierCurveTo(x, y - size * 0.3, x - size, y - size * 0.3, x - size, y + size * 0.2);
      ctx.bezierCurveTo(x - size, y + size * 0.7, x, y + size * 1.1, x, y + size * 1.1);
      ctx.bezierCurveTo(x, y + size * 1.1, x + size, y + size * 0.7, x + size, y + size * 0.2);
      ctx.bezierCurveTo(x + size, y - size * 0.3, x, y - size * 0.3, x, y + size * 0.3);
      ctx.fill();
      ctx.restore();
    }

    function animate() {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => {
        p.y += p.speedY;
        p.x += p.speedX;
        p.rotation += p.rotSpeed;
        p.twinkle += p.twinkleSpeed;
        const twinkledOpacity = p.opacity * (0.6 + 0.4 * Math.sin(p.twinkle));

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        if (isBday) {
          drawStar(ctx, 0, 0, p.size, p.color, twinkledOpacity);
        } else {
          drawHeart(ctx, 0, 0, p.size * 0.6, p.color, twinkledOpacity);
        }
        ctx.restore();

        if (p.y > H + 20) {
          p.y = -20;
          p.x = Math.random() * W;
        }
      });
      animRef.current = requestAnimationFrame(animate);
    }
    animate();
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [type]);

  return <canvas ref={canvasRef} style={{ position:'absolute', inset:0, pointerEvents:'none' }} />;
}

// ── Main component ────────────────────────────────────────
export function CelebrationOverlay() {
  const { member } = useAuth();
  const [celebrations, setCelebrations] = useState([]);
  const [current, setCurrent] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [wishModal, setWishModal] = useState(false);
  const [wishCopied, setWishCopied] = useState(false);

  // ── Persistent dismissal via localStorage (per member) ───
  const storageKey = `ucc_seen_celebrations_${member?.id || 'guest'}`;
  function getSeenIds() {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; }
  }
  function markIdSeen(id) {
    try {
      const seen = getSeenIds();
      if (!seen.includes(id)) { seen.push(id); localStorage.setItem(storageKey, JSON.stringify(seen)); }
    } catch {}
  }

  useEffect(() => {
    if (!member?.id) return;
    api.get('/notices?limit=20')
      .then(r => {
        const seen = getSeenIds();
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const celebs = (r.data.notices || []).filter(n =>
          ['birthday', 'anniversary'].includes(n.notice_type) &&
          !n.is_read &&
          !seen.includes(n.id) &&
          new Date(n.created_at).getTime() > sevenDaysAgo
        );
        if (celebs.length > 0) setCelebrations(celebs);
      })
      .catch(() => {});
  }, [member?.id]);

  async function acknowledge() {
    if (acknowledging) return;
    setAcknowledging(true);
    const celeb = celebrations[current];
    // Mark seen permanently in localStorage
    markIdSeen(celeb.id);
    // Also try server-side mark (works for targeted notices)
    try { await api.patch('/notices/' + celeb.id + '/read'); } catch {}

    if (current < celebrations.length - 1) {
      setCurrent(c => c + 1);
      setAcknowledging(false);
      setWishModal(false);
    } else {
      setDismissed(true);
    }
  }

  // Later = also permanently dismissed (per user request — should not re-appear)
  function dismissLater() {
    const celeb = celebrations[current];
    markIdSeen(celeb.id);
    if (current < celebrations.length - 1) {
      setCurrent(c => c + 1);
      setWishModal(false);
    } else {
      setDismissed(true);
    }
  }

  function copyWish() {
    const celeb = celebrations[current];
    const msg = celeb.body;
    navigator.clipboard?.writeText(msg).catch(() => {});
    setWishCopied(true);
    setTimeout(() => setWishCopied(false), 2000);
  }

  function openWhatsApp() {
    const celeb = celebrations[current];
    const msg = encodeURIComponent(celeb.body);
    window.open('https://wa.me/?text=' + msg, '_blank');
  }

  if (dismissed || celebrations.length === 0) return null;

  const celeb = celebrations[current];
  const isBday = celeb.notice_type === 'birthday';
  const isAnniversary = celeb.notice_type === 'anniversary';

  // Determine emoji and accent from content
  const titleLower = (celeb.title || '').toLowerCase();
  const isActualAnniversary = isAnniversary || titleLower.includes('anniversary');
  const isMotm = titleLower.includes('man of the match') || titleLower.includes('best batsman') || titleLower.includes('best bowler') || titleLower.includes('best fielder') || titleLower.includes('all-rounder') || titleLower.includes('man of the series');
  const isTeamWin = titleLower.includes('champion') || titleLower.includes('winners') || (isAnniversary && !titleLower.includes('anniversary'));
  const isAchievement = isBday && !titleLower.includes('birthday') && !isActualAnniversary && !isTeamWin;

  const overlayEmoji = isMotm ? '⭐' : isTeamWin ? '🏆' : isActualAnniversary ? '💍' : isAchievement ? '🏏' : isBday ? '🎂' : '💍';
  const badgeLabel = isMotm ? '⭐ Match Award' : isTeamWin ? '🏆 Team Win' : isActualAnniversary ? '💍 Anniversary' : isAchievement ? '🏏 Achievement' : isBday ? '🎉 Birthday' : '💛 Anniversary';

  // Theme: stored in file_name as 'dark', 'light', or 'custom:https://...'
  const fileNameRaw = celeb.file_name || 'dark';
  const isCustom = fileNameRaw.startsWith('custom:');
  const customImageUrl = isCustom ? fileNameRaw.replace('custom:', '') : null;
  const isLight = fileNameRaw === 'light';
  const accentColor = isActualAnniversary ? '#E8A4A4' : isBday ? '#F5C518' : (isLight ? '#D4447A' : '#E8A4A4');
  const accentGlow = isActualAnniversary ? 'rgba(232,164,164,0.3)' : isBday ? 'rgba(245,197,24,0.3)' : (isLight ? 'rgba(212,68,122,0.2)' : 'rgba(232,164,164,0.3)');

  // Light theme colors
  const cardBg = isLight
    ? 'linear-gradient(160deg, #ffffff 0%, #f8f6ff 60%, #fff9f0 100%)'
    : 'linear-gradient(160deg, #0f1729 0%, #1a2540 60%, #0f1729 100%)';
  const textColor = isLight ? '#1E3A6E' : 'white';
  const textSecondary = isLight ? 'rgba(30,58,110,0.7)' : 'rgba(255,255,255,0.8)';
  const borderColor = isLight ? accentColor + '60' : accentColor + '40';
  const overlayBg = isLight ? 'rgba(240,244,255,0.88)' : 'rgba(10,14,26,0.92)';
  const counterColor = isLight ? accentColor : accentColor + '99';
  const dividerColor = isLight ? accentColor + '60' : accentColor + '40';
  const laterBtnBg = isLight ? 'rgba(30,58,110,0.06)' : 'rgba(255,255,255,0.05)';
  const laterBtnColor = isLight ? 'rgba(30,58,110,0.4)' : 'rgba(255,255,255,0.5)';
  const laterBtnBorder = isLight ? '1px solid rgba(30,58,110,0.15)' : '1px solid rgba(255,255,255,0.15)';
  const bottomNoteColor = isLight ? 'rgba(30,58,110,0.25)' : 'rgba(255,255,255,0.2)';
  const dismissBtnBg = isLight ? 'rgba(30,58,110,0.06)' : 'rgba(255,255,255,0.08)';
  const dismissBtnColor = isLight ? 'rgba(30,58,110,0.4)' : 'rgba(255,255,255,0.4)';

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center',
      background: isCustom && customImageUrl
        ? `url(${customImageUrl}) center/cover no-repeat`
        : overlayBg,
      backdropFilter: isCustom ? 'none' : 'blur(8px)',
    }}>
      {/* Dark overlay on custom images for readability */}
      {isCustom && (
        <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.45)', zIndex:0 }} />
      )}
      <style>{`
        @keyframes slideUp {
          from { transform:translateY(40px); opacity:0; }
          to { transform:translateY(0); opacity:1; }
        }
        @keyframes glowPulse {
          0%,100% { box-shadow: 0 0 40px ${accentGlow}, 0 20px 60px rgba(0,0,0,0.6); }
          50% { box-shadow: 0 0 80px ${accentGlow}, 0 20px 60px rgba(0,0,0,0.6); }
        }

        @keyframes floatEmoji {
          0%,100% { transform:translateY(0) scale(1); }
          50% { transform:translateY(-6px) scale(1.05); }
        }
        @keyframes fadeInUp {
          from { opacity:0; transform:translateY(10px); }
          to { opacity:1; transform:translateY(0); }
        }
      `}</style>

      {!isCustom && <ParticleCanvas type={celeb.notice_type} />}

      {/* Card */}
      <div style={{
        position:'relative', zIndex:1,
        background: isCustom ? 'rgba(10,14,26,0.82)' : cardBg,
        border:`1px solid ${borderColor}`,
        borderRadius:24, padding:'40px 32px 32px',
        maxWidth:360, width:'90%', textAlign:'center',
        animation:'slideUp 0.5s cubic-bezier(0.34,1.56,0.64,1), glowPulse 3s ease-in-out infinite',
      }}>

        {/* Top accent line */}
        <div style={{
          position:'absolute', top:0, left:'50%', transform:'translateX(-50%)',
          width:'60%', height:2,
          background:`linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
          borderRadius:2,
        }} />

        {/* Counter */}
        {celebrations.length > 1 && (
          <div style={{
            position:'absolute', top:16, right:16,
            fontSize:'0.7rem', fontWeight:700, color:`${accentColor}99`,
            fontFamily:'Rajdhani,sans-serif', letterSpacing:'0.1em',
          }}>
            {current + 1} / {celebrations.length}
          </div>
        )}

        {/* Emoji */}
        <div style={{ fontSize:'3.5rem', marginBottom:16, animation:'floatEmoji 3s ease-in-out infinite', display:'inline-block' }}>
          {overlayEmoji}
        </div>

        {/* Type badge */}
        <div style={{
          display:'inline-block',
          fontSize:'0.7rem', fontWeight:800,
          letterSpacing:'0.18em', textTransform:'uppercase',
          color: isBday ? '#1a1a00' : '#5a2020',
          borderRadius:20, padding:'5px 16px', marginBottom:20,
          background: accentColor,
          boxShadow: `0 2px 12px ${accentGlow}`,
          animation:'fadeInUp 0.6s ease 0.1s both',
        }}>
          {badgeLabel}
        </div>

        {/* Name */}
        <div style={{
          fontFamily:'Rajdhani, sans-serif',
          fontSize:'2rem', fontWeight:900,
          lineHeight:1.15, marginBottom:16,
          color: isCustom ? 'white' : textColor,
          textShadow: (isLight && !isCustom) ? `0 0 20px ${accentColor}80` : `0 0 30px ${accentColor}, 0 2px 4px rgba(0,0,0,0.5)`,
          animation:'fadeInUp 0.6s ease 0.2s both',
        }}>
          {(celeb.title || '')
            .replace(/^\p{Emoji}\s*/u, '')
            .replace(/^Happy (Birthday|Anniversary)[,!]?\s*/i, '')
            .replace(/!$/, '')
            .trim()}
        </div>

        {/* Message */}
        <div style={{
          fontSize:'0.88rem', color: textSecondary,
          lineHeight:1.6, marginBottom:28,
          fontStyle:'italic',
          padding:'0 4px',
          maxHeight:'160px', overflowY:'auto',
          animation:'fadeInUp 0.6s ease 0.3s both',
        }}>
          "{celeb.body}"
        </div>

        {/* Divider */}
        <div style={{
          width:'100%', height:1,
          background:`linear-gradient(90deg, transparent, ${dividerColor}, transparent)`,
          marginBottom:20,
        }} />

        {/* Action buttons */}
        {!wishModal ? (
          <div style={{ display:'flex', gap:10, animation:'fadeInUp 0.6s ease 0.4s both' }}>
            <button onClick={() => setWishModal(true)}
              style={{
                flex:2, padding:'13px 0', borderRadius:12, border:'none',
                background:`linear-gradient(135deg, ${accentColor}, ${isBday ? '#E8A020' : '#C97B7B'})`,
                color: isBday ? '#1a1a00' : '#fff',
                fontFamily:'Rajdhani,sans-serif', fontWeight:800,
                fontSize:'0.9rem', letterSpacing:'0.1em', textTransform:'uppercase',
                cursor:'pointer',
                boxShadow:`0 4px 20px ${accentGlow}`,
              }}>
              💬 Send Wishes
            </button>
            <button onClick={dismissLater}
              style={{
                flex:1, padding:'13px 0', borderRadius:12,
                border: laterBtnBorder,
                background: laterBtnBg,
                color: laterBtnColor,
                fontFamily:'Rajdhani,sans-serif', fontWeight:700,
                fontSize:'0.8rem', letterSpacing:'0.08em', textTransform:'uppercase',
                cursor:'pointer',
              }}>
              Later
            </button>
          </div>
        ) : (
          <div style={{ animation:'fadeInUp 0.3s ease' }}>
            <div style={{ fontSize:'0.75rem', color: isLight ? 'rgba(30,58,110,0.4)' : 'rgba(255,255,255,0.4)', marginBottom:12, letterSpacing:'0.05em' }}>
              SEND WISHES VIA
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:10 }}>
              <button onClick={openWhatsApp}
                style={{
                  flex:1, padding:'12px 0', borderRadius:10, border:'none',
                  background:'#25D366', color:'white',
                  fontFamily:'Rajdhani,sans-serif', fontWeight:800,
                  fontSize:'0.85rem', cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                }}>
                <span style={{ fontSize:'1.1rem' }}>📱</span> WhatsApp
              </button>
              <button onClick={copyWish}
                style={{
                  flex:1, padding:'12px 0', borderRadius:10,
                  border:`1px solid ${accentColor}60`,
                  background:`${accentColor}15`, color: accentColor,
                  fontFamily:'Rajdhani,sans-serif', fontWeight:800,
                  fontSize:'0.85rem', cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                }}>
                <span>{wishCopied ? '✅' : '📋'}</span> {wishCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <button onClick={acknowledge}
              style={{
                width:'100%', padding:'11px', borderRadius:10,
                border:'none', background: dismissBtnBg,
                color: dismissBtnColor,
                fontFamily:'Rajdhani,sans-serif', fontWeight:600,
                fontSize:'0.78rem', letterSpacing:'0.08em',
                textTransform:'uppercase', cursor:'pointer',
              }}>
              ✓ Done — Dismiss
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

export default CelebrationOverlay;
